package br.com.texeratx.boardgametimeapp.ui.screens.active_match

import br.com.texeratx.boardgametimeapp.data.model.match.MatchListItemDTO
import br.com.texeratx.boardgametimeapp.data.model.stats.GameDurationStatsDTO
import br.com.texeratx.boardgametimeapp.data.repository.MatchRepository
import br.com.texeratx.boardgametimeapp.data.repository.StatsRepository
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ActiveMatchStatsViewModel(
    private val matchRepository: MatchRepository,
    private val statsRepository: StatsRepository
) : ScreenModel {

    private val _state = MutableStateFlow(ActiveMatchStatsUiState())
    val state = _state.asStateFlow()

    fun loadForGame(gameId: Long) {
        val current = _state.value
        if (current.isLoading && current.gameId == gameId) return
        if (current.gameId == gameId && current.stats != null && current.error == null) return

        screenModelScope.launch {
            _state.value = ActiveMatchStatsUiState(
                isLoading = true,
                gameId = gameId
            )

            val matchesDeferred = async { matchRepository.getMatches() }
            val durationDeferred = async { statsRepository.getDurationStats() }

            val matchesResult = matchesDeferred.await()
            val durationResult = durationDeferred.await()

            matchesResult.fold(
                onSuccess = { matches ->
                    val gameMatches = matches.filter { it.game.id == gameId }
                    val durationDto = durationResult.getOrNull()?.find { it.gameId == gameId }
                    _state.value = ActiveMatchStatsUiState(
                        isLoading = false,
                        gameId = gameId,
                        stats = calculateStats(gameMatches, durationDto),
                        error = null
                    )
                },
                onFailure = { error ->
                    _state.value = ActiveMatchStatsUiState(
                        isLoading = false,
                        gameId = gameId,
                        error = error.message ?: "Erro ao carregar estatisticas da partida",
                        stats = null
                    )
                }
            )
        }
    }

    private fun calculateStats(
        matches: List<MatchListItemDTO>,
        durationDto: GameDurationStatsDTO?
    ): ActiveMatchGameStats? {
        if (matches.isEmpty() && durationDto == null) return null

        val allScores = matches.flatMap { match -> match.players.map { player -> player.points } }
        if (allScores.isEmpty() && durationDto == null) return null

        val bestScore = allScores.maxOrNull()
        val averageScore = allScores.average().takeIf { !it.isNaN() }
        val worstWinningScore = matches.mapNotNull { match ->
            match.players.maxOfOrNull { player -> player.points }
        }.minOrNull()

        val winsByPlayer = mutableMapOf<Long, WinnerAggregate>()

        matches.forEach { match ->
            val winningScore = match.players.maxOfOrNull { it.points } ?: return@forEach
            match.players
                .filter { it.points == winningScore }
                .forEach { winner ->
                    val previous = winsByPlayer[winner.id]
                    winsByPlayer[winner.id] = if (previous == null) {
                        WinnerAggregate(name = winner.name, wins = 1)
                    } else {
                        previous.copy(wins = previous.wins + 1)
                    }
                }
        }

        val topWinner = winsByPlayer.values
            .sortedWith(compareByDescending<WinnerAggregate> { it.wins }.thenBy { it.name.lowercase() })
            .firstOrNull()

        return ActiveMatchGameStats(
            bestScore = bestScore,
            averageScore = averageScore,
            worstWinningScore = worstWinningScore,
            topWinnerName = topWinner?.name,
            topWinnerWins = topWinner?.wins ?: 0,
            avgDurationMin = durationDto?.avgDuration?.toInt(),
            minDurationMin = durationDto?.minDuration,
            maxDurationMin = durationDto?.maxDuration
        )
    }

    private data class WinnerAggregate(
        val name: String,
        val wins: Int
    )
}
